import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  ArrowLeft,
  BarChart3,
  FileCode,
  Building2,
  Users,
  Zap,
  RefreshCw,
  Loader2,
  AlertTriangle,
  Layers,
  GitCompare,
  DollarSign,
  Lightbulb,
  LayoutDashboard,
  X,
  Check,
  UserMinus,
  ChevronDown,
} from 'lucide-react';
import type { AppDispatch, RootState } from '@/store';
import {
  fetchSupplyDemand,
  fetchTeamCapacity,
  clearSupplyDemand,
  clearTeamCapacity,
  clearBudgetAnalysis,
  clearDetailedComparison,
  updateTeamAdjustment,
} from '@/store/slices/scenariosSlice';
import { fetchEpics } from '@/store/slices/epicsSlice';
import { fetchPrograms } from '@/store/slices/programsSlice';
import { GlassCard, GlassButton, GlassBadge, GlassPageHeader } from '@/design-system';

// Components
import SupplyDemandWidget from './components/SupplyDemandWidget';
import CapacityHeatmap from './components/CapacityHeatmap';
import OptimizationPanel from './components/OptimizationPanel';
import BudgetAnalysisWidget from './components/BudgetAnalysisWidget';
import { RecommendationsPanel } from './components/RecommendationsPanel';
import ScenarioCompareEnhanced from './components/ScenarioCompareEnhanced';

import type { TeamCapacity } from '@/types';

interface Scenario {
  id: string;
  name: string;
  description?: string;
  planningPeriodId: string;
  isBaseline: boolean;
  planningPeriod?: {
    id: string;
    name: string;
  };
  _count?: {
    allocations: number;
    featureAllocations: number;
  };
}

interface ScenarioDashboardProps {
  scenario: Scenario;
  programId?: string;
  programName?: string;
  onBack: () => void;
  onCompare?: (scenarioId: string) => void;
  allScenarios?: Scenario[];
}

type TabId = 'overview' | 'budget' | 'recommendations' | 'compare';

const tabs: { id: TabId; label: string; icon: React.ReactNode }[] = [
  { id: 'overview', label: 'Overview', icon: <LayoutDashboard className="w-4 h-4" /> },
  { id: 'budget', label: 'Budget', icon: <DollarSign className="w-4 h-4" /> },
  { id: 'recommendations', label: 'Recommendations', icon: <Lightbulb className="w-4 h-4" /> },
  { id: 'compare', label: 'Compare', icon: <GitCompare className="w-4 h-4" /> },
];

// ============================================================================
// Team Quick-Edit Popover
// ============================================================================

interface TeamQuickEditPopoverProps {
  team: TeamCapacity;
  scenarioId: string;
  position: { x: number; y: number };
  onClose: () => void;
  onRefresh: () => void;
}

const TeamQuickEditPopover: React.FC<TeamQuickEditPopoverProps> = ({
  team,
  scenarioId,
  position,
  onClose,
  onRefresh,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const popoverRef = useRef<HTMLDivElement>(null);
  const [memberCount, setMemberCount] = useState(team.memberCount || 8);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      await dispatch(updateTeamAdjustment({
        scenarioId,
        teamId: team.teamId,
        adjustedMemberCount: memberCount,
      })).unwrap();
      onRefresh();
      onClose();
    } catch {
      // error handled by slice
    } finally {
      setIsSaving(false);
    }
  };

  const handleRelease = async () => {
    setIsSaving(true);
    try {
      await dispatch(updateTeamAdjustment({
        scenarioId,
        teamId: team.teamId,
        adjustedMemberCount: 0,
        isReleased: true,
      })).unwrap();
      onRefresh();
      onClose();
    } catch {
      // error handled by slice
    } finally {
      setIsSaving(false);
    }
  };

  // Compute position to keep popover on-screen
  const style: React.CSSProperties = {
    position: 'fixed',
    left: Math.min(position.x, window.innerWidth - 280),
    top: Math.min(position.y, window.innerHeight - 260),
    zIndex: 50,
  };

  return (
    <div ref={popoverRef} style={style} className="w-64 rounded-xl bg-[var(--bg-secondary)] border border-[var(--border-subtle)] shadow-2xl p-4">
      <div className="flex items-center justify-between mb-3">
        <h4 className="text-sm font-semibold text-foreground truncate flex-1">{team.teamName}</h4>
        <button onClick={onClose} className="p-1 text-muted-foreground hover:text-foreground">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="text-xs text-muted-foreground mb-3 space-y-1">
        <div className="flex justify-between">
          <span>Utilization</span>
          <span className={
            team.utilizationPercent >= 100 ? 'text-red-400' :
            team.utilizationPercent >= 80 ? 'text-amber-400' : 'text-emerald-400'
          }>
            {team.utilizationPercent.toFixed(0)}%
          </span>
        </div>
        <div className="flex justify-between">
          <span>Allocated / Available</span>
          <span className="text-foreground">{team.allocatedWeeks.toFixed(0)} / {team.availableWeeks.toFixed(0)} TW</span>
        </div>
      </div>

      <div className="mb-3">
        <label className="text-xs text-muted-foreground mb-1 block">Member Count</label>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={0}
            max={30}
            value={memberCount}
            onChange={(e) => setMemberCount(parseInt(e.target.value))}
            className="flex-1 accent-blue-500"
          />
          <input
            type="number"
            min={0}
            max={100}
            value={memberCount}
            onChange={(e) => setMemberCount(Math.max(0, parseInt(e.target.value) || 0))}
            className="w-14 px-2 py-1 text-xs bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] rounded text-foreground text-center"
          />
        </div>
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={isSaving}
          className="flex-1 flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 border border-blue-500/20 transition-colors disabled:opacity-50"
        >
          {isSaving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
          Save
        </button>
        <button
          onClick={handleRelease}
          disabled={isSaving}
          className="flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-medium rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 border border-red-500/20 transition-colors disabled:opacity-50"
          title="Release team from scenario"
        >
          <UserMinus className="w-3 h-3" />
        </button>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

const ScenarioDashboard: React.FC<ScenarioDashboardProps> = ({
  scenario,
  programId: initialProgramId,
  programName: initialProgramName,
  onBack,
  allScenarios = [],
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const {
    supplyDemand,
    supplyDemandLoading,
    supplyDemandError,
    teamCapacity,
    teamCapacityLoading,
    teamCapacityError,
  } = useSelector((state: RootState) => state.scenarios);
  const { epics } = useSelector((state: RootState) => state.epics);
  const { programs } = useSelector((state: RootState) => state.programs);
  const user = useSelector((state: RootState) => state.auth.user);

  const [isOptimizationOpen, setIsOptimizationOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [activeTab, setActiveTab] = useState<TabId>('overview');

  // Program selection (in dashboard header)
  const [selectedProgramId, setSelectedProgramId] = useState<string>(initialProgramId || '');
  const [showProgramDropdown, setShowProgramDropdown] = useState(false);
  const programDropdownRef = useRef<HTMLDivElement>(null);

  // Compare tab state
  const [compareScenarioId, setCompareScenarioId] = useState<string>('');
  const [showComparison, setShowComparison] = useState(false);

  // Team quick-edit popover state
  const [quickEditTeam, setQuickEditTeam] = useState<TeamCapacity | null>(null);
  const [quickEditPosition, setQuickEditPosition] = useState({ x: 0, y: 0 });

  const selectedProgramName = selectedProgramId
    ? (programs || []).find(p => p.id === selectedProgramId)?.name || initialProgramName || ''
    : '';

  // Auto-select program for Program Leads with a single program
  useEffect(() => {
    if (user?.role === 'PROGRAM_LEAD' && programs?.length === 1 && !selectedProgramId) {
      setSelectedProgramId(programs[0].id);
    }
  }, [programs, user?.role, selectedProgramId]);

  // Close program dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (programDropdownRef.current && !programDropdownRef.current.contains(e.target as Node)) {
        setShowProgramDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Fetch data on mount and scenario change
  useEffect(() => {
    const pid = selectedProgramId || undefined;
    dispatch(fetchSupplyDemand({ scenarioId: scenario.id, programId: pid }));
    dispatch(fetchTeamCapacity({ scenarioId: scenario.id, programId: pid }));
    dispatch(fetchEpics({}));
    if (!programs || programs.length === 0) {
      dispatch(fetchPrograms({}));
    }

    return () => {
      dispatch(clearSupplyDemand());
      dispatch(clearTeamCapacity());
      dispatch(clearBudgetAnalysis());
      dispatch(clearDetailedComparison());
    };
  }, [dispatch, scenario.id, selectedProgramId, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey((prev) => prev + 1);
  };

  const handleOptimizationApplied = () => {
    handleRefresh();
  };

  const handleCompareScenario = () => {
    if (compareScenarioId) {
      setShowComparison(true);
    }
  };

  const handleTeamClick = (team: TeamCapacity, event?: React.MouseEvent) => {
    const rect = event?.currentTarget?.getBoundingClientRect();
    setQuickEditTeam(team);
    setQuickEditPosition({
      x: rect ? rect.right + 8 : 400,
      y: rect ? rect.top : 200,
    });
  };

  const isLoading = supplyDemandLoading || teamCapacityLoading;
  const hasError = supplyDemandError || teamCapacityError;

  // Quick stats from supply/demand data
  const quickStats = supplyDemand
    ? [
        {
          label: 'Total Supply',
          value: `${supplyDemand.supply.totalTeamWeeks.toFixed(0)} TW`,
          icon: Building2,
          color: 'text-emerald-400',
          bgColor: 'bg-emerald-500/20',
        },
        {
          label: 'Total Demand',
          value: `${supplyDemand.demand.totalTeamWeeks.toFixed(0)} TW`,
          icon: FileCode,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/20',
        },
        {
          label: 'EPICs',
          value: supplyDemand.demand.byInvestibleStructure.reduce(
            (sum, is) => sum + is.epics.length,
            0
          ),
          icon: Layers,
          color: 'text-blue-400',
          bgColor: 'bg-blue-500/20',
        },
        {
          label: 'Utilization',
          value: `${supplyDemand.gap.utilizationRate.toFixed(1)}%`,
          icon: BarChart3,
          color:
            supplyDemand.gap.status === 'OVER_CAPACITY'
              ? 'text-red-400'
              : supplyDemand.gap.status === 'NEAR_CAPACITY'
              ? 'text-amber-400'
              : 'text-green-400',
          bgColor:
            supplyDemand.gap.status === 'OVER_CAPACITY'
              ? 'bg-red-500/20'
              : supplyDemand.gap.status === 'NEAR_CAPACITY'
              ? 'bg-amber-500/20'
              : 'bg-green-500/20',
        },
      ]
    : [];

  // Available scenarios for comparison (exclude current)
  const comparableScenarios = allScenarios.filter(s => s.id !== scenario.id);

  return (
    <div className="min-h-screen">
      {/* Header */}
      <GlassPageHeader
        title={scenario.name}
        subtitle={`${scenario.planningPeriod?.name || 'Unknown Period'}${
          scenario.isBaseline ? ' (Baseline)' : ''
        }${scenario.description ? ` — ${scenario.description}` : ''}`}
        actions={
          <div className="flex items-center gap-2">
            <GlassButton variant="ghost" onClick={onBack}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </GlassButton>

            {/* Program Selector (in header) */}
            <div ref={programDropdownRef} className="relative">
              <button
                onClick={() => setShowProgramDropdown(!showProgramDropdown)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border transition-colors ${
                  selectedProgramId
                    ? 'bg-blue-500/15 text-blue-400 border-blue-500/30'
                    : 'bg-[var(--bg-tertiary)] text-muted-foreground border-[var(--border-subtle)] hover:text-foreground'
                }`}
              >
                <Building2 className="w-3.5 h-3.5" />
                {selectedProgramName || 'Select Program'}
                <ChevronDown className="w-3 h-3" />
              </button>

              {showProgramDropdown && (
                <div className="absolute right-0 top-full mt-1 w-56 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-subtle)] shadow-xl z-50 py-1 max-h-64 overflow-y-auto">
                  <button
                    onClick={() => { setSelectedProgramId(''); setShowProgramDropdown(false); }}
                    className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-hover)] transition-colors ${
                      !selectedProgramId ? 'text-blue-400 font-medium' : 'text-muted-foreground'
                    }`}
                  >
                    All Programs
                  </button>
                  {(programs || []).map(p => (
                    <button
                      key={p.id}
                      onClick={() => { setSelectedProgramId(p.id); setShowProgramDropdown(false); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-[var(--bg-hover)] transition-colors ${
                        selectedProgramId === p.id ? 'text-blue-400 font-medium' : 'text-foreground'
                      }`}
                    >
                      {p.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <GlassButton
              variant="ghost"
              onClick={handleRefresh}
              disabled={isLoading}
            >
              <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
            </GlassButton>

            {activeTab === 'overview' && (
              <GlassButton
                variant="gradient"
                onClick={() => setIsOptimizationOpen(true)}
              >
                <Zap className="w-4 h-4 mr-2" />
                Optimize
              </GlassButton>
            )}
          </div>
        }
      />

      <div className="px-6 py-6">
        {/* Tab Bar */}
        <div className="flex gap-1 p-1 rounded-xl bg-[var(--bg-tertiary)] border border-[var(--border-subtle)] mb-6">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all ${
                activeTab === tab.id
                  ? 'bg-gradient-to-r from-blue-500/20 to-violet-500/20 text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground hover:bg-[var(--bg-secondary)]'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Loading State */}
        {isLoading && !supplyDemand && activeTab === 'overview' && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 text-blue-400 animate-spin" />
            <span className="ml-3 text-muted-foreground">Loading scenario data...</span>
          </div>
        )}

        {/* Error State */}
        {hasError && activeTab === 'overview' && (
          <GlassCard className="p-6 mb-6">
            <div className="flex items-center gap-3 text-red-400">
              <AlertTriangle className="w-5 h-5" />
              <span>{supplyDemandError || teamCapacityError}</span>
            </div>
            <GlassButton
              variant="secondary"
              onClick={handleRefresh}
              className="mt-4"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Retry
            </GlassButton>
          </GlassCard>
        )}

        {/* ===================== OVERVIEW TAB ===================== */}
        {activeTab === 'overview' && supplyDemand && (
          <div className="space-y-6">
            {/* Quick Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {quickStats.map((stat, index) => {
                const Icon = stat.icon;
                return (
                  <GlassCard key={index} className="p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-muted-foreground">{stat.label}</p>
                        <p className={`text-2xl font-bold mt-1 ${stat.color}`}>
                          {stat.value}
                        </p>
                      </div>
                      <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                        <Icon className={`w-5 h-5 ${stat.color}`} />
                      </div>
                    </div>
                  </GlassCard>
                );
              })}
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <SupplyDemandWidget data={supplyDemand} className="lg:col-span-1" />

              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">
                  Demand by Investible Structure
                </h3>
                <div className="space-y-3 max-h-80 overflow-y-auto">
                  {supplyDemand.demand.byInvestibleStructure.map((is) => {
                    const fillPercent =
                      is.requestedWeeks > 0
                        ? (is.allocatedWeeks / is.requestedWeeks) * 100
                        : 0;
                    const categoryColors: Record<string, string> = {
                      BUSINESS: 'bg-blue-500',
                      TECH: 'bg-sky-500',
                      SUPPORT: 'bg-orange-500',
                    };

                    return (
                      <div key={is.isId} className="p-3 bg-[var(--bg-tertiary)]/50 rounded-lg">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {is.isName}
                            </span>
                            <GlassBadge variant="default">
                              {is.category}
                            </GlassBadge>
                          </div>
                          <span className="text-xs text-muted-foreground">
                            {is.epics.length} EPIC{is.epics.length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="flex-1 h-2 bg-[var(--bg-hover)]/50 rounded-full overflow-hidden">
                            <div
                              className={`h-full ${
                                categoryColors[is.category] || 'bg-gray-500'
                              } rounded-full transition-all`}
                              style={{ width: `${Math.min(fillPercent, 100)}%` }}
                            />
                          </div>
                          <span className="text-xs text-muted-foreground w-24 text-right">
                            {is.allocatedWeeks.toFixed(0)} / {is.requestedWeeks.toFixed(0)} TW
                          </span>
                        </div>
                        {is.gap > 0 && (
                          <p className="text-xs text-amber-400 mt-1">
                            Gap: {is.gap.toFixed(1)} TW
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </GlassCard>
            </div>

            {/* Capacity Heatmap */}
            {teamCapacity && (
              <CapacityHeatmap
                data={teamCapacity}
                onTeamClick={(team) => {
                  // Use a synthetic event position since CapacityHeatmap doesn't pass the event
                  const rect = document.querySelector(`[title*="${team.teamName}"]`)?.getBoundingClientRect();
                  handleTeamClick(team, { currentTarget: { getBoundingClientRect: () => rect || { right: 400, top: 200 } } } as any);
                }}
              />
            )}

            {/* Programs Summary */}
            {supplyDemand && (
              <GlassCard className="p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Program Supply Overview
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {supplyDemand.supply.byProgram.map((program) => {
                    const utilizationColor =
                      program.allocatedWeeks / program.availableWeeks >= 1
                        ? 'text-red-400'
                        : program.allocatedWeeks / program.availableWeeks >= 0.8
                        ? 'text-amber-400'
                        : 'text-emerald-400';

                    return (
                      <div
                        key={program.programId}
                        className="p-4 bg-[var(--bg-tertiary)]/50 rounded-lg hover:bg-[var(--bg-tertiary)] transition-colors"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <span className="font-medium text-foreground truncate">
                            {program.programName}
                          </span>
                          <Users className="w-4 h-4 text-muted-foreground" />
                        </div>
                        <div className="space-y-1 text-sm">
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Total</span>
                            <span className="text-foreground">
                              {program.totalWeeks.toFixed(0)} TW
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Available</span>
                            <span className="text-foreground">
                              {program.availableWeeks.toFixed(0)} TW
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Allocated</span>
                            <span className={utilizationColor}>
                              {program.allocatedWeeks.toFixed(0)} TW
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Remaining</span>
                            <span
                              className={
                                program.remainingWeeks < 0
                                  ? 'text-red-400'
                                  : 'text-emerald-400'
                              }
                            >
                              {program.remainingWeeks.toFixed(0)} TW
                            </span>
                          </div>
                        </div>
                        <div className="mt-3 h-1.5 bg-[var(--bg-hover)]/50 rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full transition-all ${
                              program.allocatedWeeks / program.availableWeeks >= 1
                                ? 'bg-red-500'
                                : program.allocatedWeeks / program.availableWeeks >= 0.8
                                ? 'bg-amber-500'
                                : 'bg-emerald-500'
                            }`}
                            style={{
                              width: `${Math.min(
                                (program.allocatedWeeks / program.availableWeeks) * 100,
                                100
                              )}%`,
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </GlassCard>
            )}
          </div>
        )}

        {/* ===================== BUDGET TAB ===================== */}
        {activeTab === 'budget' && (
          <>
            {selectedProgramId && selectedProgramName ? (
              <BudgetAnalysisWidget
                scenarioId={scenario.id}
                programId={selectedProgramId}
                programName={selectedProgramName}
              />
            ) : (
              <GlassCard className="p-8 text-center">
                <DollarSign className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <h3 className="text-lg font-semibold text-foreground mb-1">Select a Program</h3>
                <p className="text-muted-foreground mb-4">
                  Budget analysis requires a program selection. Use the program selector in the header above.
                </p>
                <GlassButton
                  variant="secondary"
                  onClick={() => setShowProgramDropdown(true)}
                >
                  <Building2 className="w-4 h-4 mr-2" />
                  Select Program
                </GlassButton>
              </GlassCard>
            )}
          </>
        )}

        {/* ===================== RECOMMENDATIONS TAB ===================== */}
        {activeTab === 'recommendations' && (
          <RecommendationsPanel
            scenarioId={scenario.id}
            programId={selectedProgramId || undefined}
          />
        )}

        {/* ===================== COMPARE TAB ===================== */}
        {activeTab === 'compare' && (
          <div className="space-y-6">
            <GlassCard className="p-6">
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <GitCompare className="w-5 h-5" />
                Compare Scenarios
              </h3>
              <div className="flex items-end gap-4">
                <div className="flex-1">
                  <label className="block text-sm text-muted-foreground mb-2">
                    Compare with:
                  </label>
                  <select
                    value={compareScenarioId}
                    onChange={(e) => {
                      setCompareScenarioId(e.target.value);
                      setShowComparison(false);
                    }}
                    className="w-full px-3 py-2 rounded-lg bg-[var(--bg-secondary)] border border-[var(--border-subtle)] text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/50"
                  >
                    <option value="">Select a scenario...</option>
                    {comparableScenarios.map(s => (
                      <option key={s.id} value={s.id}>
                        {s.name} {s.isBaseline ? '(Baseline)' : ''} — {s.planningPeriod?.name || 'Unknown'}
                      </option>
                    ))}
                  </select>
                </div>
                <GlassButton
                  variant="gradient"
                  onClick={handleCompareScenario}
                  disabled={!compareScenarioId}
                >
                  <GitCompare className="w-4 h-4 mr-2" />
                  Compare
                </GlassButton>
              </div>
            </GlassCard>

            {showComparison && compareScenarioId && (
              <ScenarioCompareEnhanced
                scenarioId={scenario.id}
                otherScenarioId={compareScenarioId}
                isOpen={true}
                onClose={() => setShowComparison(false)}
              />
            )}

            {!showComparison && !compareScenarioId && (
              <GlassCard className="p-8 text-center">
                <GitCompare className="w-12 h-12 text-muted-foreground mx-auto mb-3 opacity-50" />
                <p className="text-muted-foreground">
                  Select another scenario above to compare side-by-side.
                </p>
              </GlassCard>
            )}
          </div>
        )}
      </div>

      {/* Team Quick-Edit Popover */}
      {quickEditTeam && (
        <TeamQuickEditPopover
          team={quickEditTeam}
          scenarioId={scenario.id}
          position={quickEditPosition}
          onClose={() => setQuickEditTeam(null)}
          onRefresh={handleRefresh}
        />
      )}

      {/* Optimization Panel */}
      <OptimizationPanel
        scenarioId={scenario.id}
        scenarioName={scenario.name}
        epics={epics}
        isOpen={isOptimizationOpen}
        onClose={() => setIsOptimizationOpen(false)}
        onOptimizationApplied={handleOptimizationApplied}
      />
    </div>
  );
};

export default ScenarioDashboard;
